import { useEffect, useState } from "react";
import {
  Zap, MapPin, Phone, Clock, Wifi, Coffee, ShieldCheck,
  Users, Navigation, ArrowRight, Battery, Sparkles, Menu, X, Bolt, ChevronRight, Sun, Moon
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ev-charge-hub-35/artifacts/u6g9q630_logo1.webp";
const BANNER_LIGHT = "https://customer-assets.emergentagent.com/job_ev-charge-hub-35/artifacts/tg31qg5z_1stbannerlight.png";
const BANNER_DARK = "https://customer-assets.emergentagent.com/job_ev-charge-hub-35/artifacts/grvwvt26_1stbannerdark.png";

const STATION_MAP_URL = "https://www.google.com/maps?q=17.0897250,79.7803120";

const NAV_ITEMS = [
  { label: "Why NexVolt", href: "#why" },
  { label: "How it Works", href: "#how" },
  { label: "Locations", href: "#locations" },
  { label: "Contact", href: "#contact" },
];

const FEATURES = [
  { icon: Bolt, title: "360 KW DC Fast Chargers", desc: "Ultra-fast charging to power your journey in minutes, not hours." },
  { icon: Clock, title: "Open 24/7", desc: "Round-the-clock availability whenever the road calls." },
  { icon: Battery, title: "Multiple Chargers", desc: "Multiple charging points so you never have to wait in line." },
  { icon: Users, title: "Manned Stations", desc: "Trained on-site staff to assist with every session." },
  { icon: Wifi, title: "Free WiFi", desc: "Stay productive or stream while your EV recharges." },
  { icon: Coffee, title: "Premium Amenities", desc: "Clean restrooms, refreshments, and a comfortable lounge." },
];

const STEPS = [
  { num: "01", title: "Find a Hub", desc: "Locate the nearest NexVolt charging hub with real-time updates on availability." },
  { num: "02", title: "Plug & Power", desc: "Connect your EV to our 360 KW DC fast charger and start charging instantly." },
  { num: "03", title: "Flexible Payments", desc: "Pay via UPI, cards, or digital wallets — whichever is convenient for you." },
  { num: "04", title: "Hit the Road", desc: "Get a full charge in record time and continue your journey worry-free." },
];

const LOCATIONS = [
  {
    name: "Ram NexVolt EV Charging Station",
    status: "OPEN 24/7",
    address: "Madharavaram NH 65 Highway, Munagala Mandalam, Suryapet",
    coords: "17.0897250° N, 79.7803120° E",
    available: "3/4",
    amenities: ["Free WiFi", "Food Court", "Parking", "Clean Restrooms"],
    state: "live",
    mapUrl: STATION_MAP_URL,
  },
  {
    name: "Khammam – Devarapalli Highway",
    status: "COMING SOON",
    address: "Devarapalli Highway, Khammam, Telangana",
    coords: "Planning Stage",
    available: "—",
    amenities: ["360 KW Fast Chargers", "Premium Lounge", "Coffee Bar", "Highway Access"],
    state: "planning",
    mapUrl: null,
  },
];

const STATS = [
  { value: "360", suffix: " KW", label: "DC Fast Charging" },
  { value: "100", suffix: "+", label: "Fast Chargers" },
  { value: "24/7", suffix: "", label: "Availability" },
  { value: "10K", suffix: "+", label: "Happy Riders" },
];

function BrandMark({ size = 44, withText = true, theme = "dark", highlight = false }) {
  return (
    <div className="flex items-center gap-3">
      <div className={highlight ? "brand-logo-halo" : ""} style={{ width: size, height: size }}>
        <img
          src={LOGO_URL}
          alt="NexVolt EV"
          style={{ width: size, height: size }}
          className="object-contain"
        />
      </div>
      {withText && (
        <div className="leading-none">
          <div className="brand-wordmark">
            <span className={theme === "dark" ? "text-white" : "text-slate-900"}>Nex</span>
            <span className="text-[var(--brand-cyan)]">Volt</span>
            <span className="brand-ev">EV</span>
          </div>
          <div className="brand-tag">Powering your next journey</div>
        </div>
      )}
    </div>
  );
}

function ThemeToggle({ theme, setTheme }) {
  return (
    <button
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className="theme-toggle"
      data-testid="theme-toggle-btn"
      aria-label="Toggle theme"
    >
      <span className={`theme-knob ${theme === "light" ? "is-light" : ""}`}>
        {theme === "dark" ? <Moon size={14} /> : <Sun size={14} />}
      </span>
      <span className="theme-toggle-label mono">{theme === "dark" ? "DARK" : "LIGHT"}</span>
    </button>
  );
}

function Nav({ theme, setTheme }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-blur py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center" data-testid="nav-logo">
          <BrandMark size={94} theme={theme} highlight />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className="text-sm font-medium nav-link relative group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-gradient-to-r from-[var(--brand-cyan)] to-[var(--brand-green)] transition-all group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <a href="#contact" data-testid="nav-visit-btn" className="btn-primary !py-2.5 !px-5 text-sm">
            Visit us <ArrowRight size={16} />
          </a>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <button
            onClick={() => setOpen(!open)}
            className="text-current p-2"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden mt-3 mx-4 nav-blur rounded-2xl p-4 border border-token">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className="block py-3 nav-link border-b border-token last:border-0"
            >
              {item.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="btn-primary w-full justify-center mt-3"
          >
            Visit us <ArrowRight size={16} />
          </a>
        </div>
      )}
    </nav>
  );
}

function Hero({ theme }) {
  const banner = theme === "dark" ? BANNER_DARK : BANNER_LIGHT;
  return (
    <section id="top" className="relative pt-32 pb-24 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="lightning-glow" style={{ width: 500, height: 500, top: -100, left: -150, background: "#1e6bff" }} />
      <div className="lightning-glow" style={{ width: 400, height: 400, top: 100, right: -100, background: "#2cf07e" }} />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="eyebrow rise rise-1" data-testid="hero-eyebrow">
            <span className="pulse-dot" /> Most Reliable EV Charging Network
          </div>

          <h1 className="headline mt-6 rise rise-2" data-testid="hero-title">
            <span className="hero-fg">Powering your</span>{" "}
            <span className="grad-text">next journey</span>
          </h1>

          <p className="mt-7 text-lg subtle max-w-xl leading-relaxed rise rise-3" data-testid="hero-subtitle">
            Premium charging hubs along your favorite highways and cities. 360 KW DC fast chargers,
            24/7 availability, and amenities that make every stop a pleasant one.
          </p>

          <div className="mt-9 flex flex-wrap gap-4 rise rise-4">
            <a href="#contact" className="btn-primary" data-testid="hero-visit-btn">
              <MapPin size={18} /> Visit us
            </a>
            <a href="#locations" className="btn-ghost" data-testid="hero-find-btn">
              Find a Hub <ArrowRight size={16} />
            </a>
          </div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 rise rise-5" data-testid="hero-stats">
            {STATS.map((s) => (
              <div key={s.label} className="border-l border-token pl-4">
                <div className="text-3xl font-bold hero-fg tracking-tight">
                  {s.value}<span className="text-[var(--brand-green)]">{s.suffix}</span>
                </div>
                <div className="text-xs mono uppercase tracking-wider subtle mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 relative rise rise-3" data-testid="hero-visual">
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[var(--brand-blue)]/20 via-transparent to-[var(--brand-green)]/20 blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-token shadow-2xl">
              <img
                src={banner}
                alt="NexVolt EV Charging Station"
                className="w-full h-auto block"
              />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between px-4 py-3 rounded-xl bg-black/55 backdrop-blur border border-white/15">
                <div className="flex items-center gap-2">
                  <Bolt size={16} className="text-[var(--brand-green)]" />
                  <span className="text-xs mono uppercase tracking-wider text-white">360 KW Active</span>
                </div>
                <span className="pulse-dot" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MarqueeBelt() {
  const items = ["FAST CHARGING", "24/7 OPEN", "360 KW DC", "PAN-INDIA", "PREMIUM AMENITIES", "POWERING YOUR NEXT JOURNEY"];
  const looped = [...items, ...items, ...items];
  return (
    <div className="border-y border-token py-5 overflow-hidden marquee-bg">
      <div className="marquee-track">
        {looped.map((t, i) => (
          <div key={i} className="flex items-center gap-3 subtle mono uppercase text-sm tracking-[0.2em]">
            <Zap size={16} className="text-[var(--brand-green)]" />
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

function WhyChoose() {
  return (
    <section id="why" className="relative py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <div className="eyebrow"><Sparkles size={12} /> Why Choose NexVolt</div>
          <h2 className="section-title mt-5 hero-fg" data-testid="why-title">
            India's most reliable<br />
            <span className="grad-text">EV charging experience</span>
          </h2>
          <p className="mt-5 text-lg subtle max-w-2xl">
            State-of-the-art infrastructure built for modern electric vehicles — designed around speed,
            comfort, and the open road.
          </p>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="why-features">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="glass-card p-7" data-testid={`feature-${f.title.toLowerCase().replace(/ /g, "-")}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[var(--brand-blue)]/20 to-[var(--brand-green)]/10 border border-token">
                  <Icon size={22} className="text-[var(--brand-cyan)]" />
                </div>
                <h3 className="mt-5 text-xl font-semibold hero-fg">{f.title}</h3>
                <p className="mt-2 text-sm subtle leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="relative py-28 how-bg">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="eyebrow"><Zap size={12} /> Simple Process</div>
            <h2 className="section-title mt-5 hero-fg" data-testid="how-title">
              How NexVolt <span className="grad-text">works</span>
            </h2>
          </div>
          <p className="lg:col-span-5 subtle text-lg">
            Get from low battery to full charge in four effortless steps — no hassle, no app required.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="how-steps">
          {STEPS.map((s, idx) => (
            <div key={s.num} className="glass-card p-7 relative">
              <div className="text-5xl font-bold mono text-transparent" style={{ WebkitTextStroke: "1px rgba(58,169,255,0.5)" }}>
                {s.num}
              </div>
              <h3 className="mt-4 text-xl font-semibold hero-fg">{s.title}</h3>
              <p className="mt-2 text-sm subtle leading-relaxed">{s.desc}</p>
              {idx < STEPS.length - 1 && (
                <ChevronRight size={20} className="hidden lg:block absolute -right-4 top-1/2 text-[var(--brand-cyan)]/40" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Locations() {
  return (
    <section id="locations" className="relative py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <div className="eyebrow"><MapPin size={12} /> Our Network</div>
          <h2 className="section-title mt-5 hero-fg" data-testid="locations-title">
            Charging <span className="grad-text">locations</span>
          </h2>
          <p className="mt-5 text-lg subtle">
            Strategic hubs across highways — live now, and expanding to new corridors soon.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-6" data-testid="locations-grid">
          {LOCATIONS.map((loc) => (
            <div
              key={loc.name}
              className="glass-card p-8 relative overflow-hidden"
              data-testid={`location-${loc.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div
                className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-30"
                style={{ background: loc.state === "live" ? "#2cf07e" : "#3aa9ff" }}
              />
              <div className="relative">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-2xl font-bold hero-fg leading-tight">{loc.name}</h3>
                  <span
                    className={`mono text-[10px] tracking-[0.15em] px-3 py-1 rounded-full border whitespace-nowrap ${
                      loc.state === "live"
                        ? "bg-[var(--brand-green)]/10 border-[var(--brand-green)]/40 text-[var(--brand-green)]"
                        : "bg-[var(--brand-cyan)]/10 border-[var(--brand-cyan)]/40 text-[var(--brand-cyan)]"
                    }`}
                  >
                    {loc.status}
                  </span>
                </div>
                <div className="mt-4 flex items-start gap-2 text-sm hero-fg">
                  <MapPin size={16} className="text-[var(--brand-cyan)] mt-0.5 flex-shrink-0" />
                  <span>{loc.address}</span>
                </div>
                <div className="mt-1 mono text-xs subtle ml-6">{loc.coords}</div>

                <div className="my-6 divider-line" />

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs mono uppercase subtle tracking-wider">Available Chargers</div>
                    <div className="mt-1 text-3xl font-bold hero-fg">
                      {loc.available}
                      {loc.state === "live" && <span className="text-sm subtle ml-1">slots</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs mono uppercase subtle tracking-wider">Status</div>
                    <div className="mt-1 flex items-center gap-2">
                      {loc.state === "live" ? (
                        <>
                          <span className="pulse-dot" />
                          <span className="text-sm hero-fg">Live now</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-[var(--brand-cyan)]" />
                          <span className="text-sm hero-fg">In planning</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {loc.amenities.map((a) => (
                    <span
                      key={a}
                      className="text-xs px-3 py-1.5 rounded-full chip-amenity"
                    >
                      {a}
                    </span>
                  ))}
                </div>

                {loc.state === "live" && loc.mapUrl && (
                  <a
                    href={loc.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-7 inline-flex items-center gap-2 text-sm text-[var(--brand-cyan)] hover:text-[var(--brand-green)] transition-colors"
                    data-testid="navigate-station-btn"
                  >
                    <Navigation size={15} /> Navigate to Station
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact({ theme }) {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    setForm({ name: "", phone: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section id="contact" className="relative py-28">
      <div className="lightning-glow" style={{ width: 600, height: 600, top: 100, left: "50%", transform: "translateX(-50%)", background: "#1e6bff" }} />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="eyebrow mx-auto"><Phone size={12} /> Get in Touch</div>
          <h2 className="section-title mt-5 hero-fg" data-testid="contact-title">
            Visit us at <span className="grad-text">NexVolt</span>
          </h2>
          <p className="mt-5 text-lg subtle">
            Drop by our station or give us a call — we're here to power your next journey.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 glass-card p-8" data-testid="contact-info">
            <BrandMark size={62} theme={theme} highlight />

            <div className="space-y-6 mt-7">
              <div>
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-wider subtle">
                  <MapPin size={14} className="text-[var(--brand-cyan)]" /> Visit Us
                </div>
                <p className="mt-2 hero-fg text-base leading-relaxed">
                  NexVolt EV Charging Station<br />
                  Flat No 107, My Envirise Apartment,<br />
                  Pocharam, Hyderabad
                </p>
                <a
                  href="https://www.google.com/maps?q=My+Envirise+Apartment+Pocharam+Hyderabad"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-sm text-[var(--brand-cyan)] hover:text-[var(--brand-green)]"
                  data-testid="map-link"
                >
                  Open in Google Maps <ArrowRight size={14} />
                </a>
              </div>

              <div className="divider-line" />

              <div>
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-wider subtle">
                  <Phone size={14} className="text-[var(--brand-cyan)]" /> Call Us
                </div>
                <a
                  href="tel:+917093803399"
                  className="mt-2 block text-2xl font-bold hero-fg hover:text-[var(--brand-green)] transition-colors"
                  data-testid="phone-link"
                >
                  +91 70938 03399
                </a>
              </div>

              <div className="divider-line" />

              <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--brand-green)]/5 border border-[var(--brand-green)]/20">
                <ShieldCheck size={20} className="text-[var(--brand-green)] flex-shrink-0" />
                <p className="text-sm subtle">
                  On-site staff available for assistance during all charging hours.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 glass-card p-8" data-testid="contact-form">
            <h3 className="text-2xl font-bold hero-fg">Send us a message</h3>
            <p className="subtle text-sm mt-1">We'll respond as soon as possible.</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs mono uppercase tracking-wider subtle">Your Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="John Doe"
                  className="field mt-2"
                  data-testid="form-name"
                />
              </div>
              <div>
                <label className="text-xs mono uppercase tracking-wider subtle">Phone Number *</label>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="field mt-2"
                  data-testid="form-phone"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs mono uppercase tracking-wider subtle">Your Message *</label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us about your EV charging needs..."
                className="field mt-2 resize-none"
                data-testid="form-message"
              />
            </div>

            <button type="submit" className="btn-primary mt-6 w-full justify-center" data-testid="form-submit">
              {sent ? "Message Sent" : "Send Message"} <ArrowRight size={16} />
            </button>
            {sent && (
              <p className="mt-3 text-center text-sm text-[var(--brand-green)]" data-testid="form-success">
                Thanks! We'll get back to you shortly.
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer({ theme }) {
  return (
    <footer className="border-t border-token mt-10 pt-14 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <BrandMark size={68} theme={theme} highlight />
            <p className="mt-5 text-sm subtle max-w-sm leading-relaxed">
              Premium EV charging hubs across South India — designed for speed, reliability,
              and the open road.
            </p>
          </div>

          <div>
            <div className="text-xs mono uppercase tracking-[0.2em] subtle">Visit</div>
            <p className="mt-4 text-sm hero-fg leading-relaxed">
              Flat No 107, My Envirise Apartment,<br />
              Pocharam, Hyderabad
            </p>
          </div>

          <div>
            <div className="text-xs mono uppercase tracking-[0.2em] subtle">Contact</div>
            <a href="tel:+917093803399" className="mt-4 block text-lg font-semibold hero-fg hover:text-[var(--brand-green)]">
              +91 70938 03399
            </a>
            <div className="mt-4 flex gap-3">
              <a href="#locations" className="btn-ghost !py-2 !px-4 text-xs">Find Hub</a>
              <a href="#contact" className="btn-primary !py-2 !px-4 text-xs">Visit us</a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-token flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs subtle mono uppercase tracking-wider">
            © {new Date().getFullYear()} NexVolt EV. All rights reserved.
          </p>
          <p className="text-xs subtle flex items-center gap-2">
            <Zap size={12} className="text-[var(--brand-green)]" /> Powering your next journey
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("nexvolt-theme") || "dark";
    }
    return "dark";
  });

  useEffect(() => {
    document.title = "NexVolt EV — Powering your next journey";
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("nexvolt-theme", theme);
  }, [theme]);

  return (
    <div data-testid="landing-page" data-theme={theme} className="app-shell min-h-screen">
      <Nav theme={theme} setTheme={setTheme} />
      <Hero theme={theme} />
      <MarqueeBelt />
      <WhyChoose />
      <HowItWorks />
      <Locations />
      <Contact theme={theme} />
      <Footer theme={theme} />
    </div>
  );
}
